import React, { useState } from "react";
import {
  Modal,
  Button,
  Form,
  Space,
  Steps,
  Result,
  Select,
  message,
  Alert,
} from "antd";
import { useStepsForm } from "sunflower-antd";

import NutunPaymentInfo from "../PaymentType/NutunPaymentInfo";
import NupayPaymentInfo from "../PaymentType/NupayPaymentInfo";
import PayAtPaymentInfo from "../PaymentType/Pay@PaymentInfo";
import {
  addSecondaryPaymentApi,
  addCashPaymentTypeApi,
} from "../../../services/Payment/PaymentApis";
import { invalidateCompliance } from "../../../utils/complianceInvalidate";

import { SelectProps } from "antd/lib/select";

const { Step } = Steps;

interface IModalProps {
  show: boolean;
  showModal: Function;
  handleAdd: Function;
  handleEdit: Function;
  memberId: string;
  /** When true, adds a second payment method (PayAt via API or DebiCheck via Nupay). */
  isSecondary?: boolean;
  /** Primary payment type — used to filter secondary choices. */
  primaryPaymentType?: string | null;
}

const primaryPaymentTypes: SelectProps["options"] = [
  {
    name: "Payment Type",
    label: "Nupay",
    value: "Nupay",
  },
  {
    name: "Payment Type",
    label: "PayAt",
    value: "PayAt",
  },
  {
    name: "Payment Type",
    label: "Cash",
    value: "Cash",
  },
];

const AddPaymentMethodModal: React.FC<IModalProps> = ({
  show,
  showModal,
  handleAdd: _handleAdd,
  handleEdit: _handleEdit,
  memberId,
  isSecondary = false,
  primaryPaymentType,
}) => {
  const [loading, setLoading] = useState(false);
  const [paymentType, setPaymentType] = useState("");
  const [secondaryError, setSecondaryError] = useState("");

  const primaryIsPayAt =
    String(primaryPaymentType || "").toLowerCase() === "payat";

  const secondaryPaymentTypes: SelectProps["options"] = primaryIsPayAt
    ? [
        {
          name: "Payment Type",
          label: "Nupay (DebiCheck)",
          value: "Nupay",
        },
      ]
    : [
        {
          name: "Payment Type",
          label: "PayAt",
          value: "PayAt",
        },
      ];

  const paymentTypes = isSecondary
    ? secondaryPaymentTypes
    : primaryPaymentTypes;

  const {
    form,
    current,
    gotoStep,
    stepsProps,
    formProps,
    submit,
  } = useStepsForm({
    isBackValidate: false,
    async submit() {
      return true;
    },
    total: 4,
  });

  const handleModal = () => {
    showModal();
    gotoStep(0);
    form.resetFields();
    setPaymentType("");
    setSecondaryError("");
  };

  const handleChangePayment = (e: any) => {
    setPaymentType(e);
  };

  const handleSecondaryPayAt = async () => {
    setLoading(true);
    setSecondaryError("");
    try {
      const response = await addSecondaryPaymentApi(String(memberId));
      if (!response?.success) {
        setSecondaryError(
          response?.message ||
            "Could not add second payment method. Please try again."
        );
        setLoading(false);
        gotoStep(3);
        return;
      }
      message.success(
        response.message || "Pay@ added as second payment method."
      );
      invalidateCompliance();
      setLoading(false);
      gotoStep(2);
    } catch {
      setLoading(false);
      setSecondaryError("Could not add second payment method. Please try again.");
      gotoStep(3);
    }
  };

  const handleCashContinue = async () => {
    if (!memberId) {
      message.error("Member is required before recording Cash payment type.");
      return;
    }
    setLoading(true);
    setSecondaryError("");
    try {
      const res = await addCashPaymentTypeApi(String(memberId));
      if (!res?.success) {
        setSecondaryError(res?.message || "Unable to save Cash payment type.");
        setLoading(false);
        gotoStep(3);
        return;
      }
      message.success("Cash payment type saved.");
      invalidateCompliance();
      setLoading(false);
      gotoStep(2);
    } catch (err: any) {
      setSecondaryError(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to save Cash payment type."
      );
      setLoading(false);
      gotoStep(3);
    }
  };

  const getPaymentComponent = (type: string) => {
    if (isSecondary && type === "PayAt") {
      return (
        <>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="Add PayAt as second payment method"
            description="This will initiate PayAt for this member alongside the existing payment method."
          />
          <Form.Item
            labelCol={{ span: 8 }}
            wrapperCol={{ offset: 8, span: 8 }}
          >
            <Space direction="vertical" style={{ width: "100%" }}>
              <Button
                type="primary"
                block
                size="large"
                loading={loading}
                onClick={handleSecondaryPayAt}
              >
                Add PayAt
              </Button>
              <Button block size="large" onClick={() => gotoStep(0)}>
                Back
              </Button>
            </Space>
          </Form.Item>
        </>
      );
    }

    switch (type) {
      case "Nutun":
        return (
          <NutunPaymentInfo
            buttonLoading={loading}
            gotoStep={gotoStep}
            form={form}
            update={false}
            submit={submit}
            memberId={memberId}
          />
        );
      case "Nupay":
        return (
          <NupayPaymentInfo
            buttonLoading={loading}
            gotoStep={gotoStep}
            form={form}
            update={false}
            submit={submit}
            memberId={memberId}
          />
        );
      case "PayAt":
        return (
          <PayAtPaymentInfo
            buttonLoading={loading}
            gotoStep={gotoStep}
            form={form}
            update={false}
            submit={submit}
            memberId={memberId}
          />
        );
      case "Cash":
        return (
          <>
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message="Cash payer"
              description="No bank mandate is required. This records Cash as the payment type on the policy. You can still record cash receipts later from the member Payments tab."
            />
            <Form.Item
              labelCol={{ span: 8 }}
              wrapperCol={{ offset: 8, span: 8 }}
            >
              <Space direction="vertical" style={{ width: "100%" }}>
                <Button
                  type="primary"
                  block
                  size="large"
                  loading={loading}
                  onClick={handleCashContinue}
                >
                  Continue
                </Button>
                <Button
                  block
                  size="large"
                  disabled={loading}
                  onClick={() => gotoStep(0)}
                >
                  Back
                </Button>
              </Space>
            </Form.Item>
          </>
        );
      default:
        return <div>Please select a valid payment provider.</div>;
    }
  };

  // Step Styling
  const layout = {
    labelCol: { span: 6 },
    wrapperCol: { span: 17 },
    labelAlign: "right" as const,
  };

  const tailLayout = {
    labelCol: { span: 8 },
    wrapperCol: { offset: 8, span: 8 },
  };

  const display: any = [
    <>
      {isSecondary && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Choose the type for the second payment method"
          description={
            primaryIsPayAt
              ? "Primary is PayAt — add Nupay (DebiCheck) as the second method."
              : "Primary is DebiCheck — add PayAt as the second method."
          }
        />
      )}
      <Form.Item
        name="PaymentType"
        label="Payment Type"
        rules={[{ required: true }]}
        wrapperCol={{ span: 8 }}
      >
        <Select
          style={{ width: "100%" }}
          size="large"
          placeholder="Select Payment Type"
          options={paymentTypes}
          onChange={handleChangePayment}
        />
      </Form.Item>
      <Form.Item {...tailLayout}>
        <Space direction="vertical" style={{ width: "100%" }}>
          <Button
            block
            size="large"
            disabled={!paymentType}
            onClick={() => gotoStep(current + 1)}
          >
            Next
          </Button>
        </Space>
      </Form.Item>
    </>,
    getPaymentComponent(paymentType),
  ];

  return (
    <Modal
      title={isSecondary ? "Add 2nd Payment Method" : "Add Payment Method"}
      open={show}
      onOk={handleModal}
      onCancel={handleModal}
      width={900}
      footer={null}
    >
      <div style={{ display: "flex", gap: 24 }}>
        {current !== 2 && current !== 3 && (
          <div
            style={{
              flexShrink: 0,
              borderRight: "1px solid #f0f0f0",
              paddingRight: 16,
              whiteSpace: "nowrap",
            }}
          >
            <Steps {...stepsProps} direction="vertical">
              <Step title="Choose Provider" />
              <Step title="Payment Info" />
            </Steps>
          </div>
        )}
        <div style={{ flex: 1, marginTop: 24 }}>
          <Form {...layout} {...formProps} style={{ maxWidth: 900 }}>
            {display[current]}
          </Form>

          {current === 2 && (
            <Result status="success" title="Successfully submitted">
              <Space direction="vertical" style={{ width: "100%" }}>
                <Button block size="large" onClick={handleModal}>
                  Okay
                </Button>
              </Space>
            </Result>
          )}

          {current === 3 && (
            <Result
              status="error"
              title={secondaryError || "Error occurred, please try again."}
            >
              <Space direction="vertical" style={{ width: "100%" }}>
                <Button block size="large" onClick={handleModal}>
                  Okay
                </Button>
              </Space>
            </Result>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default AddPaymentMethodModal;
